import React from 'react';
import styled, { css } from 'styled-components';
import { defaultTheme } from '../../../utils/defaultTheme';
import { basePath } from '../../../services/utils';

const BrandUploadButtonsContainer = styled.div`
    margin: 75px 35px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Button = styled.div`
    width: 115px;
    height: 40px;
    font-weight: 600;
    box-sizing: border-box;
    padding: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-weight: 600;
    border-radius: 3px;

    border: ${props => (props.disabled ? 'none' : `1px solid ${defaultTheme.blue[950]}`)};
    box-shadow: ${props => (props.disabled ? 'none' : `0px 0px 4px ${defaultTheme.black[29]}`)};
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    opacity: ${props => (props.disabled ? 0.7 : 1)};
    background: ${props => (props.disabled ? defaultTheme.grey[70] : defaultTheme.white.ff)};
    color: ${props => (props.disabled ? defaultTheme.blue['950-52'] : defaultTheme.blue[950])};

    transition: box-shadow 0.1s ease-in-out;
    user-select: none;

    &:hover {
        background: ${props => (props.disabled ? defaultTheme.grey[300] : defaultTheme.white.ff)};
        box-shadow: 0px 0px 10px ${defaultTheme.black[29]};
    }

    &:active {
        background: ${props => (props.disabled ? defaultTheme.grey[300] : defaultTheme.grey[50])};
    }

    p {
        text-align: center;
        width: 100%;
        font-size: 14px;
    }
`;

export const SortIconWrapper = styled.div`
    display: block;
    width: 9px;
    margin-right: 4px;
    & span {
        height: 14px;
        width: 9px;
        display: inline-block;
        vertical-align: middle;
    }
    @media (max-width: 1500px) {
        display: inline-block;
    }
`;

const NonSorted = (
    <SortIconWrapper className="sortIconWrapper">
        <span
            style={{
                backgroundImage: `url(${basePath}/visuals/Sort_off.png)`,
            }}
        >
            {' '}
        </span>
    </SortIconWrapper>
);
const SortedAsc = (
    <SortIconWrapper className="sortIconWrapper">
        <span
            style={{
                backgroundImage: `url(${basePath}/visuals/Sort_on_up.png)`,
            }}
        >
            {' '}
        </span>
    </SortIconWrapper>
);
const SortedDesc = (
    <SortIconWrapper className="sortIconWrapper">
        <span
            style={{
                backgroundImage: `url(${basePath}/visuals/Sort_on_down.png)`,
            }}
        >
            {' '}
        </span>
    </SortIconWrapper>
);

const BackOfficeTableWrapper = styled.div`
    ${({ hasDisabledColumns }) =>
        hasDisabledColumns &&
        css`
            div[role='row']:first-of-type div:nth-child(n + 3) {
                //Disable only 'User' And 'Errors' column's span that has an image
                //And avoid hiding other spans that are bellow the user and columns arrows.
                span:not(.highlight_wrapper, .highlight_span) {
                    margin-left: -6px;
                    visibility: hidden;
                }
            }
        `}

    //Centering the data in the cells and the table head
    div[role='cell'] {
        max-width: 150px;
        overflow: hidden;
        word-break: break-word;
        padding: 10px 5px;
        justify-content: center;
        align-items: center;
        display: flex;
    }
    div[role='cell']:nth-child(1) {
        div {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }
    div[role='cell']:last-child {
        div {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;
            white-space: normal;
            text-overflow: ellipsis;
        }
    }
    div[role='cell']:nth-child(5) {
        word-break: break-word;
    }
    div[role='row'] {
        text-align: center;
    }
    div[role='rowgroup'] {
        height: 450px;
        overflow-y: scroll;
    }
    width: 100%;

    @media screen and (max-width: 1492px) {
        div[role='row'] > div:nth-of-type(2) {
            //Selecting 'Timestamp' and fixing it not having the sorting arrow above
            width: 50px !important;
            flex: 50 0 auto !important;
        }
    }
    div[role='table'] {
        min-width: -webkit-fill-available !important;
        border: none;
        box-shadow: none;
    }

    div[role='columnheader'] {
        display: flex;
        justify-content: center;
        padding: 5px;
        margin: auto 0;
        font-size: 15px;
        min-width: 150px;
        max-width: 150px;
        width: 150px;
        flex: none;
        text-align: left;
        &:last-of-type {
            margin-right: 20px;
        }
        div:first-of-type {
            margin: auto 4px auto 0;
        }
        div:last-of-type {
            width: min-content;
            margin: 0;
        }

        // Hide first div in third columnheader
        &:nth-child(n + 3) {
            div:first-of-type {
                display: none;
            }
        }
    }

    //Table Search
    .table-search {
        top: 254px;
        left: 290px;
        position: absolute;
        width: 185px;
        height: 40px;
        border: solid 6px ${({ theme }) => theme.white.ff};
        box-shadow: 0px 0px 4px ${({ theme }) => theme.black[29]};
        border-radius: 2px;
        min-width: 210px;
        & input,
        div {
            height: 35px;
            padding-left: 10px;
            font-weight: 600;
        }
    }
`;

const TABLE_STYLE = {
    tableHead: {
        backgroundColor: 'null',
        borderBottom: `2px solid ${defaultTheme.blue[300]}`,
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        color: defaultTheme.blue[950],
        borderTopRightRadius: '0',
        borderTopLeftRadius: '0',
        fontSize: '15px',
    },
    bodyRow: {
        activeColor: null,
        backgroundColor: defaultTheme.grey[50],
        backgroundColorSecondary: defaultTheme.white.ff,
        hoverColor: 'null',
    },
    column: { separatorColor: 'none' },
    global: {
        fontFamily: 'Source Sans Pro,Open Sans',
    },
};
const customStyle = {
    textColor: defaultTheme.blue[950],
    opacity: 0.4,
};
const BrandColorBlock = styled.div`
    width: 15px;
    height: 15px;
    border-radius: 3px;
    margin-left: 5px;
    background: ${({ color }) => color};
`;

export {
    BackOfficeTableWrapper,
    BrandUploadButtonsContainer,
    Button,
    NonSorted,
    SortedAsc,
    SortedDesc,
    TABLE_STYLE,
    customStyle,
    BrandColorBlock,
};
